import React from 'react';
import { Card, CardBody, CardTitle, Row, Col, Progress } from 'reactstrap';
import DailyCashFlowChart from '../DailyCashFlowChart';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';

const KPICard = ({ title, value, percentage, target, type, description }) => {
    // Determine color based on target
    let color = 'primary';
    let statusText = '';

    if (type === 'solvency') {
        // Target 100%
        if (percentage >= 100) { color = 'success'; statusText = 'Solvente'; }
        else { color = 'danger'; statusText = 'Déficit'; }
    } else if (type === 'effectiveness') {
        // Target 85%
        if (percentage >= 85) { color = 'success'; statusText = 'Excelente'; }
        else if (percentage >= 70) { color = 'warning'; statusText = 'Aceptable'; }
        else { color = 'danger'; statusText = 'Bajo'; }
    }

    return (
        <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm h-full">
            <div className="flex justify-between items-start mb-2">
                <div>
                    <h5 className="text-gray-500 text-xs font-bold uppercase tracking-wide">{title}</h5>
                    <div className="flex items-baseline gap-2 mt-1">
                        <span className={`text-2xl font-bold text-${color === 'warning' ? 'orange-500' : (color === 'danger' ? 'red-500' : 'green-600')}`}>
                            {percentage.toFixed(1)}%
                        </span>
                        {type === 'solvency' ? (
                            percentage >= 100 ? <TrendingUp size={16} className="text-green-500" /> : <TrendingDown size={16} className="text-red-500" />
                        ) : (
                            <Target size={16} className="text-gray-400" />
                        )}
                    </div>
                </div>
                <div className={`p-2 rounded-full bg-${color}-100 bg-opacity-20`}>
                    {type === 'solvency' ? '💰' : '🎯'}
                </div>
            </div>

            <p className="text-xs text-gray-400 mb-3">{description}</p>

            <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
                <div
                    className={`h-2 rounded-full ${color === 'warning' ? 'bg-orange-500' : (color === 'danger' ? 'bg-red-500' : 'bg-green-500')}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                ></div>
            </div>
            <div className="text-right">
                <small className="text-xs font-bold text-gray-400">Meta: {target}%</small>
            </div>
        </div>
    );
};

const DailyCashFlowSection = ({ data, kpis, loading }) => {
    if (loading) {
        return <div className="p-8 text-center text-gray-400">Cargando flujo de caja...</div>;
    }

    if (!data || data.length === 0) {
        return null; // Or show empty state
    }

    return (
        <Row className="mb-4">
            <Col xs="12">
                <Card className="shadow-sm border-0 bg-white">
                    <CardBody className="p-5">
                        <div className="flex flex-col md:flex-row gap-6 mb-6 items-stretch">
                            {/* Left: KPIs */}
                            <div className="w-full md:w-1/4 flex flex-col gap-4">
                                <h3 className="font-bold text-gray-800 text-lg mb-2">Indicadores Financieros</h3>

                                <KPICard
                                    title="Solvencia (Cobertura)"
                                    percentage={kpis.solvency || 0}
                                    target={100}
                                    type="solvency"
                                    description="Capacidad de cubrir egresos con ingresos del periodo."
                                />

                                <KPICard
                                    title="Efectividad Recaudo"
                                    percentage={kpis.effectiveness || 0}
                                    target={85}
                                    type="effectiveness"
                                    description="Ingresos reales vs Ventas esperadas (con IVA)."
                                />
                            </div>

                            {/* Right: Chart */}
                            <div className="w-full md:w-3/4">
                                <DailyCashFlowChart
                                    incomeData={data.map(d => ({ date: d.date, value: d.income }))}
                                    outflowData={data.map(d => ({ date: d.date, value: d.expense }))}
                                />
                            </div>
                        </div>

                        {/* Summary Footer */}
                        <div className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-4 mt-2">
                            <div className="text-center">
                                <p className="text-xs text-gray-400 uppercase font-bold">Total Ingresos</p>
                                <p className="text-xl font-bold text-green-600">
                                    {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(kpis.total_income)}
                                </p>
                            </div>
                            <div className="text-center border-l border-r border-gray-100">
                                <p className="text-xs text-gray-400 uppercase font-bold">Total Egresos</p>
                                <p className="text-xl font-bold text-red-500">
                                    {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(kpis.total_expense)}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-gray-400 uppercase font-bold">Flujo Neto</p>
                                <p className={`text-xl font-bold ${kpis.net_result >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                    {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(kpis.net_result)}
                                </p>
                            </div>
                        </div>

                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default DailyCashFlowSection;
